import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

/**
 * Hook to track Branch.io pageview API call completion
 * Shows loader until https://api2.branch.io/v1/pageview completes
 */
export function useBranchPageview() {
  const [isBranchLoading, setIsBranchLoading] = useState(true);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsBranchLoading(false);
      return;
    }

    // Reset loading state on route change
    setIsBranchLoading(true);
    setHasTimedOut(false);

    let timeoutId;
    let intervalId;
    let isCompleted = false;
    const originalFetch = window.fetch;
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    // Set a maximum timeout (e.g., 3 seconds) to prevent infinite loading
    const MAX_WAIT_TIME = 3000;
    timeoutId = setTimeout(() => {
      if (!isCompleted) {
        isCompleted = true;
        setHasTimedOut(true);
        setIsBranchLoading(false);
      }
    }, MAX_WAIT_TIME);

    const markComplete = () => {
      if (!isCompleted) {
        isCompleted = true;
        setIsBranchLoading(false);
        clearTimeout(timeoutId);
        if (intervalId) clearInterval(intervalId);
      }
    };

    // Method 1: Intercept fetch requests
    window.fetch = function(...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      
      if (url.includes('api2.branch.io/v1/pageview')) {
        return originalFetch.apply(this, args)
          .then((response) => {
            markComplete();
            return response;
          })
          .catch((error) => {
            markComplete();
            throw error;
          });
      }
      
      return originalFetch.apply(this, args);
    };

    // Method 2: Intercept XMLHttpRequest (Branch SDK might use this)
    let xhrUrl = '';
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      xhrUrl = url;
      return originalXHROpen.apply(this, [method, url, ...rest]);
    };

    XMLHttpRequest.prototype.send = function(...args) {
      if (xhrUrl && xhrUrl.includes('api2.branch.io/v1/pageview')) {
        this.addEventListener('loadend', markComplete);
        this.addEventListener('error', markComplete);
        this.addEventListener('abort', markComplete);
      }
      return originalXHRSend.apply(this, args);
    };

    // Method 3: Use Performance API to check for completed requests
    const checkPerformanceEntries = () => {
      try {
        const entries = performance.getEntriesByType('resource');
        const branchPageview = entries.find(
          (entry) => entry.name && entry.name.includes('api2.branch.io/v1/pageview')
        );
        
        if (branchPageview) {
          // Check if the request has completed (responseEnd > 0 means it finished)
          if (branchPageview.responseEnd > 0) {
            markComplete();
          }
        }
      } catch (e) {
        // Performance API might not be available
      }
    };

    // Check periodically for completed requests
    intervalId = setInterval(checkPerformanceEntries, 50);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalXHROpen;
      XMLHttpRequest.prototype.send = originalXHRSend;
    };
  }, [router.asPath]); // Reset when route changes

  return { isBranchLoading, hasTimedOut };
}

